import { useState, useCallback, useEffect } from "react"
import { Trans, useTranslation } from "react-i18next"
import { downloadExport } from "../../../helpers/downloadExport"
import { Badge, Group, Text, ActionIcon, TextInput, Select, Button, Tooltip, Card, Box, Stack } from "@mantine/core"
import { DateInput } from "@mantine/dates"
import { useParams } from "react-router-dom"
import { DataTable, useDataTable } from "../../../components/DataTable"
import type { Column } from "../../../components/DataTable"
import { useDocumentCategories, useDocumentTypes } from "../hooks/useDocuments"
import { DocumentSchema } from "../models/document"
import type { Document } from "../models/document"
import { useUser } from "../../user/hooks/useUser"
import { PageLayout, PageHeader } from "../../../components/crud"
import DocumentCategorySidebar from "./DocumentCategorySidebar"
import DocumentDrawer from "./DocumentDrawer"
import type { DrawerMode } from "./DocumentDrawer"

export default function DocumentList() {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageDocuments ?? false
  const params = useParams<{ "*"?: string }>()
  const routeId = params["*"] ? Number(params["*"].split("/")[0]) : NaN

  const { data, isLoading, page, pageSize, sort, sortDir, handleSortChange, handlePageChange, filters, updateFilters } =
    useDataTable<Document>({
      queryKey: "documents",
      url: "document",
      schema: DocumentSchema,
      defaultSort: { field: "id", dir: "desc" },
      defaultPageSize: 50,
    })

  const { data: categoriesData } = useDocumentCategories()
  const { data: typesData } = useDocumentTypes()
  const activeCategoryId = (filters.category_id as number) ?? null

  const [drawerOpened, setDrawerOpened] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("view")
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null)

  const openDrawer = useCallback((id: number | null, mode: DrawerMode) => {
    setSelectedDocId(id)
    setDrawerMode(mode)
    setDrawerOpened(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setDrawerOpened(false)
    setSelectedDocId(null)
  }, [])

  const handleDeleted = useCallback(() => {
    closeDrawer()
  }, [closeDrawer])

  useEffect(() => {
    if (!Number.isNaN(routeId) && routeId > 0) {
      openDrawer(routeId, "view")
    }
  }, [routeId, openDrawer])

  const handleRowClick = useCallback((v: Document) => {
    openDrawer(v.id, "view")
  }, [openDrawer])

  const handleCategoryChange = (id: number | null) => {
    updateFilters({ category_id: id })
  }

  const categoryOptions = (categoriesData ?? []).map((c: { id: number; name: string }) => ({
    value: String(c.id),
    label: c.name,
  }))

  const mimeTypeOptions = (typesData?.mime_types ?? []).map((m: { type: string; title: string }) => ({
    value: m.type,
    label: m.title,
  }))

  const columns: Column<Document>[] = [
    {
      key: "mime_type",
      label: "",
      width: 40,
      render: (v) => (
        <Tooltip label={v.mime_type_title}>
          <Text component="span" c="dimmed">
            <i className={`${v.mime_type_class}`} />
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "description",
      label: t("documents.description"),
      sortable: true,
      render: (v) => (
        <Text fw={500}>
          {v.description.length > 50 ? `${v.description.substring(0, 50)}...` : v.description}
        </Text>
      ),
    },
    {
      key: "category_id",
      label: t("documents.category"),
      sortable: true,
      render: (v) =>
        v.category_name ? (
          <Text size="sm">{v.category_name.length > 20 ? `${v.category_name.substring(0, 20)}...` : v.category_name}</Text>
        ) : (
          <Text size="sm" fs="italic" c="gray.5">
            (não definido)
          </Text>
        ),
    },
    {
      key: "created_at",
      label: t("documents.createdAt"),
      sortable: true,
      render: (v) => (v.created_at ? new Date(v.created_at).toLocaleDateString("pt-PT") : "-"),
    },
    {
      key: "valid_until",
      label: t("documents.validUntil"),
      sortable: true,
      render: (v) =>
        v.valid_until ? (
          <Text size="sm">{new Date(v.valid_until).toLocaleDateString("pt-PT")}</Text>
        ) : (
          <Text size="sm" c="dimmed">-</Text>
        ),
      visible: canManage,
    },
    {
      key: "public",
      label: t("documents.public"),
      sortable: true,
      width: 80,
      render: (v) =>
        v.public ? (
          <Badge variant="light" color="green" size="sm">
            <i className="fa fa-check" />
          </Badge>
        ) : null,
      visible: canManage,
    },
  ]

  return (
    <PageLayout
      header={
        <PageHeader
          title={t("documents.title")}
          actions={
            <Group>
              <Button
                variant="default"
                onClick={() => downloadExport("document/export", "documentos.xlsx")}
                leftSection={<i className="fa fa-file-excel-o" />}
              >
                <Trans i18nKey="common.export" />
              </Button>
              {canManage && (
                <Button onClick={() => openDrawer(null, "create")} leftSection={<i className="fa fa-plus" />}>
                  <Trans i18nKey="documents.create" />
                </Button>
              )}
            </Group>
          }
        />
      }
    >
      <Group wrap="nowrap" align="flex-start" gap="md">
        <Card
          w={260}
          style={{ flexShrink: 0, position: "sticky", top: 0 }}
          withBorder
          p="xs"
          radius="md"
        >
          <DocumentCategorySidebar
            categories={categoriesData ?? []}
            activeCategoryId={activeCategoryId}
            onCategoryChange={handleCategoryChange}
          />
        </Card>

        <Box style={{ flex: 1, minWidth: 0 }}>
      <Stack gap="md">
      <Card withBorder p="sm" radius="md">
        <Group grow align="flex-end">
          <TextInput
            label={t("documents.filterDescription")}
            placeholder={t("documents.searchDescription")}
            onChange={(e) => updateFilters({ description: e.currentTarget.value })}
          />
          <Select
            label={t("documents.filterMimeType")}
            placeholder={t("documents.filterMimeType")}
            data={mimeTypeOptions}
            clearable
            onChange={(val) => updateFilters({ mime_type: val })}
          />
          <Select
            label={t("documents.filterPublic")}
            placeholder={t("documents.filterPublic")}
            data={[
              { value: "1", label: t("documents.public") },
              { value: "0", label: t("documents.private") },
            ]}
            clearable
            onChange={(val) => updateFilters({ public: val })}
          />
          <DateInput
            label={t("documents.filterStartDate")}
            placeholder=""
            valueFormat="YYYY-MM-DD"
            clearable
            value={filters.start_date ? new Date(filters.start_date as string) : null}
            onChange={(val) => updateFilters({ start_date: val ? val.toISOString().split("T")[0] : undefined })}
          />
          <DateInput
            label={t("documents.filterEndDate")}
            placeholder=""
            valueFormat="YYYY-MM-DD"
            clearable
            value={filters.end_date ? new Date(filters.end_date as string) : null}
            onChange={(val) => updateFilters({ end_date: val ? val.toISOString().split("T")[0] : undefined })}
          />
        </Group>
      </Card>

      <DataTable<Document>
        columns={columns.filter((c) => c.visible !== false)}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        sort={sort}
        sortDir={sortDir}
        loading={isLoading}
        emptyMessage={t("documents.empty")}
        keyExtractor={(v) => v.id}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        actions={(v) => (
          <Group gap={4} justify="flex-end">
            <Tooltip label={t("common.download")}>
              <ActionIcon
                variant="subtle"
                size="md"
                component="a"
                href={v.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa fa-download" />
              </ActionIcon>
            </Tooltip>
            {canManage && (
              <>
                <Tooltip label={t("common.edit")}>
                  <ActionIcon variant="subtle" size="md" onClick={() => openDrawer(v.id, "edit")}>
                    <i className="fa fa-pencil" />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
        )}
      />
      </Stack>
        </Box>
      </Group>

      <DocumentDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        mode={drawerMode}
        selectedDocId={selectedDocId}
        onDeleted={handleDeleted}
        onSwitchToEdit={() => setDrawerMode("edit")}
      />
    </PageLayout>
  )
}
